/**
 * Validation for the startup intake form.
 *
 * Mirrors the API's `createStartupSchema` so a field is rejected here rather
 * than after a round trip — but the API validates independently, because a
 * client-side schema is a convenience and never the control.
 *
 * PRD §10.1 targets under two minutes to log a startup, so only the name is
 * required. A form demanding twelve fields is a form people avoid, and the
 * database then stops being a record of the funnel.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.4, PRD §10.1
 */
import { z } from 'zod';

/** Turns an empty string into undefined, so a blank optional field is absent. */
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();

export const startupFormSchema = z.object({
  name: z.string().trim().min(1, 'A name is required').max(200),

  /* Validated as a URL only when something was typed: an empty optional field
     must not report "invalid URL". */
  website: z
    .string()
    .trim()
    .max(500)
    .refine(
      (value) => value === '' || z.url().safeParse(value).success,
      'Enter a full URL, e.g. https://example.com',
    )
    .transform((value) => (value === '' ? undefined : value))
    .optional(),

  hqCountry: z
    .string()
    .trim()
    .refine(
      (value) => value === '' || value.length === 2,
      'Use a two-letter ISO country code, e.g. PT',
    )
    .transform((value) => (value === '' ? undefined : value.toUpperCase()))
    .optional(),

  /* Kept as a string in the form and coerced here: a number input reports ''
     for an empty field, and `Number('')` is 0 — which would silently log a
     TRL of zero as though it had been chosen. */
  trl: z
    .string()
    .trim()
    .refine((value) => {
      if (value === '') return true;
      const parsed = Number(value);
      return Number.isInteger(parsed) && parsed >= 1 && parsed <= 9;
    }, 'TRL runs from 1 to 9')
    .transform((value) => (value === '' ? undefined : Number(value)))
    .optional(),

  description: optionalText(2_000),
  differentiator: optionalText(2_000),
});

/** The form's input shape — every field a string, as the DOM provides them. */
export type StartupFormInput = z.input<typeof startupFormSchema>;

/** The parsed shape, ready to send. */
export type StartupFormOutput = z.output<typeof startupFormSchema>;
